import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  SecurityContext,
  ViewEncapsulation,
} from '@angular/core';
import { File } from '../../../../../shared/models/file';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import {
  MessageBoxComponent,
  MessageBoxParameters,
  MessageBoxType,
} from 'app/components/messageBox/messageBox.component';
import { KeyValuePair } from 'app/shared/models/keyvaluepair';
import {
  AttachmentService,
  enumAttachmentType,
} from 'app/shared/services/attachment.service';
import { SubjectUrl } from 'app/shared/models/subject-url';
import { UrlDialogComponent } from '../../url-dialog/url-dialog.component';
import { IParentEntity } from 'app/shared/interfaces/entity';
import { DomSanitizer } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import {
  HyperLawService,
  CasePrepExportStatus,
} from 'app/shared/services/hyper-law.service';

@Component({
  selector: 'file-list-item',
  templateUrl: './file-list-item.component.html',
  styleUrls: ['./file-list-item.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class FileListItemComponent implements OnInit, OnDestroy {
  @Input() item: File | SubjectUrl;
  @Input() readOnly: boolean;
  @Input() userCanDeleteAttachments: boolean;
  @Input() onSubjectDashboard: boolean;
  @Input() parent: IParentEntity;
  @Input() selected: boolean;
  showCasePrepUI = false;
  onHyperLawConfigValidChanged: Subscription;

  showViewFileButton = true;
  selectable = true;
  removable = true;
  addOnBlur = true;
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  labels: string[] = [];
  summary: string = '';
  editMode = false;
  itemType = enumAttachmentType.File;
  minISODate: string = new Date('2000-1-1').toISOString();
  showLastModifiedDate = true;
  public attachmentType: typeof enumAttachmentType = enumAttachmentType;
  path: string = '';
  casePrepExportSuccess = false;
  itemHasExportStatus = false;

  constructor(
    private attachmentService: AttachmentService,
    public dialog: MatDialog,
    private sanitized: DomSanitizer,
    private hyperLawService: HyperLawService
  ) {
    this.showCasePrepUI = this.hyperLawService.isConfigValid();
    this.onHyperLawConfigValidChanged =
      this.hyperLawService.configValid.subscribe((isActive: boolean) => {
        this.showCasePrepUI = isActive;
      });
  }

  ngOnInit() {
    this.itemType = this.item.attachmentType;
    if (this.itemType === enumAttachmentType.File) {
      this.item = <File>new File(<File>this.item);
      this.showLastModifiedDate =
        new Date(this.item.lastModifiedDate).toISOString() > this.minISODate;
      this.path = this.item.path.split('\\').splice(3).join('\\');
      this.labels = this.item.labels ? this.item.labels.split(',') : [];
      this.summary = this.item.summary ? this.item.summary : '';
      this.itemHasExportStatus = this.item.casePrepExportStatus > 0;
      this.casePrepExportSuccess =
        this.item.casePrepExportStatus === CasePrepExportStatus.Success;
    } else {
      this.item = <SubjectUrl>this.item;
      this.labels = this.item.url.labels ? this.item.url.labels.split(',') : [];
      this.summary = this.item.url.summary ? this.item.url.summary : '';
    }
  }

  editUrl() {
    this.dialog
      .open(UrlDialogComponent, {
        data: { parent: this.parent, subjectUrl: this.item },
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((_) => {});
  }

  downloadFile() {
    this.attachmentService
      .getDownloadLink(this.item)
      .subscribe((url: string) => {
        // This will allow the menu to close, and the url to be opened in new window
        setTimeout(() => {
          window.open(
            <string>this.sanitized.sanitize(SecurityContext.URL, url)
          );
        }, 100);
      });
  }

  viewFile(event) {
    this.attachmentService
      .getViewOnlineLink(this.item)
      .subscribe((url: string) => {
        // This will allow the menu to close, and the url to be opened in new window
        setTimeout(() => {
          window.open(this.sanitized.sanitize(SecurityContext.URL, url));
        }, 100);
      });
  }

  itemClick() {
    if (!this.editMode) {
      this.attachmentService.toggleSelectFile(this.item);
    }
  }

  itemDblClick() {
    if (!this.editMode) {
      this.attachmentService
        .getViewOnlineLink(this.item)
        .subscribe((url: string) => {
          // This will allow the menu to close, and the url to be opened in new window
          setTimeout(() => {
            window.open(
              <string>this.sanitized.sanitize(SecurityContext.URL, url)
            );
          }, 100);
        });
    }
  }

  deleteFile(event) {
    const isFile = this.itemType === enumAttachmentType.File ? 'File' : 'URL';
    const msg =
      (isFile && this.showCasePrepUI && (this.item as File).casePrepExportStatus > 10
        ? 'This file will also be removed from Case Preparation. '
        : '') +
      'Are you sure you want to delete this ' +
      (isFile ? 'file' : 'URL') +
      '?';

    const info = new MessageBoxParameters(
      msg,
      'Confirm',
      MessageBoxType.Confirm,
      true
    );
    msg.concat();
    this.dialog
      .open(MessageBoxComponent, {
        data: info,
        disableClose: false,
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result === 'confirm') {
          this.attachmentService.deleteAttachment(this.item, this.parent.uid);
        }
      });
  }

  deselectOtherFiles() {
    this.attachmentService.deselectAll();
  }

  onSummaryChange(e) {
    this.summary = e;
    if (this.itemType === enumAttachmentType.File) {
      (this.item as File).summary = e;
    } else if (this.itemType === enumAttachmentType.Url) {
      (this.item as SubjectUrl).url.summary = e;
    }
  }

  editLabels() {
    this.attachmentService.deselectAll();
    this.editMode = true;
  }

  closeEdit() {
    this.editMode = false;
    const metadata: KeyValuePair[] = [];
    metadata.push(new KeyValuePair('Labels', this.labels.join(', ')));
    metadata.push(new KeyValuePair('Summary', this.summary));
    this.attachmentService.updateMetadata(this.item, metadata);
  }

  addLabel(event: any): void {
    const input = event.input;
    const value = event.value;

    // Add label
    if ((value || '').trim()) {
      this.labels.push(value.trim());
    }

    // Reset the input value
    if (input) {
      input.value = '';
    }
  }

  removeLabel(label: string): void {
    const index = this.labels.indexOf(label);

    if (index >= 0) {
      this.labels.splice(index, 1);
    }
  }

  ngOnDestroy(): void {
    if (this.onHyperLawConfigValidChanged) {
      this.onHyperLawConfigValidChanged.unsubscribe();
    }
  }
}
